import React, { FC, useState } from 'react';
import { Formik, FormikHelpers, FormikProps, useFormikContext } from 'formik';
import { BasicContainer, FlexCol, FlexRow, PrimaryButton } from '@minecraft.atoms';
import { useTranslation } from '@blocs.i18n';
import { captureExceptionEvent } from '@blocs.observability';
import { AddressChange } from '@blocs.address-suggestion';
import { UseBilling } from '../../useBilling';
import { CheckoutProps } from '../types';
import { PaymentMethods } from '../PaymentMethods';
import { BillingAddressCard, hasCompleteBillingAddress } from '../BillingAddressCard';
import {
  AddressValidationForm,
  applySuggestedAddressToFormik,
  BillingAddressFields,
  UseAddressValidator,
  useAddressValidator,
} from '../../AddressValidation';
import { PaywallFunnelEvents } from '../../usePaywallFunnelEvents';
import { SignupAddressValidationEvents } from '../../useSignupAddressValidationEvents';
import { BillingAddressFormLayout } from './BillingAddressFormLayout';
import { createBillingAddressValidationSchema } from './ValidationSchema';
import { formatBillingPostalCode } from './formatBillingPostalCode';

export type WalletPickerProps = {
  isWalletVisible: boolean;
  isBillingAddressEditable: boolean;
  wallet?: CheckoutProps['wallet'];
  selectedPaymentOption?: CheckoutProps['selectedPaymentOption'];
  onSelectPaymentOption?: CheckoutProps['onSelectPaymentOption'];
  setShowAddPaymentMethod?: CheckoutProps['setShowAddPaymentMethod'];
  onUpdatePaymentOption?: UseBilling['onUpdatePaymentOption'];
  funnelEvents: PaywallFunnelEvents;
  addressValidationEvents?: SignupAddressValidationEvents;
  onAddressIssueDetected?: () => void;
};

const seedAddressValues = (selectedPaymentOption?: UseBilling['wallet'][0]): BillingAddressFields => ({
  /* eslint-disable camelcase */
  postal_code: selectedPaymentOption?.postalCode ?? '',
  address_line_1: selectedPaymentOption?.street1 ?? '',
  address_line_2: selectedPaymentOption?.street2 ?? '',
  city: selectedPaymentOption?.city ?? '',
  state_region: selectedPaymentOption?.region ?? '',
  /* eslint-enable camelcase */
  country: selectedPaymentOption?.country ?? '',
});

const applyAddressChange = (
  formik: FormikProps<BillingAddressFields>,
  change: AddressChange,
  funnelEvents: PaywallFunnelEvents
) => {
  if (change.line1Only === true) {
    formik.setFieldValue('address_line_1', change.address.address1);
    return;
  }

  funnelEvents.trackBillingAddressAutofillUsed();
  const { address } = change;
  const country = address.country || formik.values.country;
  formik.setValues({
    ...formik.values,
    // eslint-disable-next-line camelcase
    address_line_1: address.address1,
    // eslint-disable-next-line camelcase
    address_line_2: address.address2,
    city: address.city,
    // eslint-disable-next-line camelcase
    state_region: address.state,
    // eslint-disable-next-line camelcase
    postal_code: formatBillingPostalCode(address.postalCode, country),
    country,
  });
};

// allowCancel is false when the selected card has no complete address on file — there's no prior
// valid address to revert to, so the user is forced through to a successful Update instead.
type EditingBillingAddressProps = {
  onCancel: () => void;
  allowCancel: boolean;
  pendingAddressIssue: UseAddressValidator['pendingAddressIssue'];
  resolveAddressIssue: UseAddressValidator['resolveAddressIssue'];
  dismissAddressIssue: UseAddressValidator['dismissAddressIssue'];
  funnelEvents: PaywallFunnelEvents;
  addressValidationEvents?: SignupAddressValidationEvents;
};

const EditingBillingAddress: FC<EditingBillingAddressProps> = ({
  onCancel,
  allowCancel,
  pendingAddressIssue,
  resolveAddressIssue,
  dismissAddressIssue,
  funnelEvents,
  addressValidationEvents,
}) => {
  const { t } = useTranslation();
  const formik = useFormikContext<BillingAddressFields>();
  const { isSubmitting, isValid } = formik;
  const isUpdateDisabled = isSubmitting || !isValid;

  return (
    <FlexCol w="full" gap="4" data-testid="billing-address-form">
      <BillingAddressFormLayout
        isBillingAddressFieldEnabled
        onAddressLine1Change={(change) => applyAddressChange(formik, change, funnelEvents)}
        funnelEvents={funnelEvents}
      />
      <FlexRow w="full" gap="2" justifyContent="flex-end" wrap="nowrap">
        {allowCancel && (
          <PrimaryButton
            type="button"
            variant="secondary"
            label={t('common:button.cancel')}
            onClick={onCancel}
            disabled={isSubmitting}
            isSmall
          />
        )}
        <PrimaryButton
          type="button"
          data-testid="update-billing-address-button"
          label={t('common:button.update')}
          onClick={() => formik.handleSubmit()}
          disabled={isUpdateDisabled}
          isSmall
        />
      </FlexRow>
      {pendingAddressIssue && (
        <AddressValidationForm
          result={pendingAddressIssue.result}
          currentAddress={pendingAddressIssue.currentAddress}
          lastCheckedInput={pendingAddressIssue.lastCheckedInput}
          onSubmit={(suggestedAddress) => {
            applySuggestedAddressToFormik(formik, suggestedAddress);
            resolveAddressIssue(suggestedAddress);
          }}
          onDismiss={dismissAddressIssue}
          onSuggestionSelected={() => addressValidationEvents?.trackAddressValidationSuggestionClicked()}
        />
      )}
    </FlexCol>
  );
};

type WalletBillingAddressEditorProps = {
  selectedPaymentOption?: UseBilling['wallet'][0];
  onUpdatePaymentOption: UseBilling['onUpdatePaymentOption'];
  funnelEvents: PaywallFunnelEvents;
  addressValidationEvents?: SignupAddressValidationEvents;
  onAddressIssueDetected?: () => void;
};

// Owns its own local <Formik>, separate from PaymentForm's. Keying it by isEditing remounts a fresh
// instance seeded from selectedPaymentOption on every Edit/Cancel, so no imperative seed/reset is
// needed. The outer WalletPicker keys this whole component by the selected card's id, so switching
// cards resets back to the view state the same way.
//
// A card with no complete address (hasCompleteBillingAddress false) starts straight in the editing
// state instead of the read-only view, and Cancel is hidden — there's no valid prior address to fall
// back to, so the user is forced through to a successful Update. AddPayment.tsx separately disables
// the checkout CTA for the same reason, so this isn't the only thing stopping a purchase, but it stops
// the user from dismissing the address prompt without ever filling it in.
//
// Instantiates its own useAddressValidator, independent of the main add-card flow's own instance
// (useRegistrationAddPayment.ts/useCheckoutAddPayment.ts/PaymentMethodsManager.tsx) — same reusable
// hook+View pair, used here right next to this form. checkAddress has to live here (handleUpdate is
// this Formik's own onSubmit, wired at this level), but applying a correction doesn't — that's
// EditingBillingAddress's job below, since it already has the Formik access to do it directly.
const WalletBillingAddressEditor: FC<WalletBillingAddressEditorProps> = ({
  selectedPaymentOption,
  onUpdatePaymentOption,
  funnelEvents,
  addressValidationEvents,
  onAddressIssueDetected,
}) => {
  const { t } = useTranslation();
  const hasAddress = hasCompleteBillingAddress(selectedPaymentOption);
  const [isEditing, setIsEditing] = useState(() => !hasAddress);
  const { checkAddress, pendingAddressIssue, resolveAddressIssue, dismissAddressIssue } = useAddressValidator({
    funnelEvents: addressValidationEvents,
    onAddressIssueDetected,
  });

  const handleUpdate = async (values: BillingAddressFields, { setSubmitting }: FormikHelpers<BillingAddressFields>) => {
    const accepted = await checkAddress(values);
    if (!accepted) {
      setSubmitting(false);
      return;
    }

    try {
      await onUpdatePaymentOption({
        billingInfoId: selectedPaymentOption?.id?.toString(),
        isPrimaryPaymentMethod: selectedPaymentOption?.isPrimaryPaymentMethod,
        isBackupPaymentMethod: selectedPaymentOption?.isBackupPaymentMethod,
        address: {
          street1: values.address_line_1,
          street2: values.address_line_2,
          city: values.city,
          region: values.state_region,
          country: values.country,
          postalCode: values.postal_code,
        },
      });
      setIsEditing(false);
    } catch (error) {
      captureExceptionEvent(error, { location: 'WalletPicker > updateBillingAddress' });
    } finally {
      setSubmitting(false);
    }
  };

  if (!selectedPaymentOption) return null;

  return (
    <Formik
      key={String(isEditing)}
      initialValues={seedAddressValues(selectedPaymentOption)}
      validationSchema={createBillingAddressValidationSchema(t)}
      validateOnMount
      onSubmit={handleUpdate}
    >
      {isEditing ? (
        <EditingBillingAddress
          onCancel={() => setIsEditing(false)}
          allowCancel={hasAddress}
          pendingAddressIssue={pendingAddressIssue}
          resolveAddressIssue={resolveAddressIssue}
          dismissAddressIssue={dismissAddressIssue}
          funnelEvents={funnelEvents}
          addressValidationEvents={addressValidationEvents}
        />
      ) : (
        <BillingAddressCard
          selectedPaymentOption={selectedPaymentOption}
          onEdit={() => {
            funnelEvents.trackBillingAddressEditClicked();
            setIsEditing(true);
          }}
        />
      )}
    </Formik>
  );
};

export const WalletPicker: FC<WalletPickerProps> = ({
  isWalletVisible,
  isBillingAddressEditable,
  wallet,
  selectedPaymentOption,
  onSelectPaymentOption,
  setShowAddPaymentMethod,
  onUpdatePaymentOption,
  funnelEvents,
  addressValidationEvents,
  onAddressIssueDetected,
}) => {
  return (
    <>
      {isWalletVisible && (
        <BasicContainer w="full" padding={4} borderRadius="md" borderPlacement="all">
          <PaymentMethods
            wallet={wallet}
            selectedPaymentOption={selectedPaymentOption}
            onSelectPaymentOption={onSelectPaymentOption}
            onAddPaymentMethod={() => {
              onSelectPaymentOption?.(undefined);
              setShowAddPaymentMethod?.(true);
            }}
          />
        </BasicContainer>
      )}
      {isBillingAddressEditable && (
        <BasicContainer w="full" padding={4} borderRadius="md" borderPlacement="all">
          <WalletBillingAddressEditor
            key={selectedPaymentOption?.id}
            selectedPaymentOption={selectedPaymentOption}
            onUpdatePaymentOption={onUpdatePaymentOption}
            funnelEvents={funnelEvents}
            addressValidationEvents={addressValidationEvents}
            onAddressIssueDetected={onAddressIssueDetected}
          />
        </BasicContainer>
      )}
    </>
  );
};
